import { clamp } from './fmath';

/**
 * A closed polyline the AI can follow: the main centre line, or a variant
 * through a shortcut (design.md B5.2, B7). Arithmetic only.
 */
export interface PathSeg {
  ax: number;
  ay: number;
  dx: number;
  dy: number;
  len: number;
  len2: number;
  ux: number;
  uy: number;
  s0: number;
}

export class Path {
  readonly segs: PathSeg[] = [];
  readonly length: number;
  /** True for routes that leave the main circuit (lanes are disabled there). */
  readonly shortcut: boolean;

  constructor(points: [number, number][], shortcut = false) {
    this.shortcut = shortcut;
    let s = 0;
    for (let i = 0; i < points.length; i++) {
      const [ax, ay] = points[i];
      const [bx, by] = points[(i + 1) % points.length];
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;
      const len = Math.sqrt(len2);
      this.segs.push({ ax, ay, dx, dy, len, len2, ux: dx / len, uy: dy / len, s0: s });
      s += len;
    }
    this.length = s;
  }

  /** Distance along the path of the nearest point, plus which segment. */
  project(x: number, y: number): { s: number; seg: number; d: number } {
    let best = Infinity;
    let bi = 0;
    let bt = 0;
    for (let i = 0; i < this.segs.length; i++) {
      const sg = this.segs[i];
      const t = clamp(((x - sg.ax) * sg.dx + (y - sg.ay) * sg.dy) / sg.len2, 0, 1);
      const px = sg.ax + sg.dx * t - x;
      const py = sg.ay + sg.dy * t - y;
      const d2 = px * px + py * py;
      if (d2 < best) {
        best = d2;
        bi = i;
        bt = t;
      }
    }
    const sg = this.segs[bi];
    return { s: sg.s0 + sg.len * bt, seg: bi, d: Math.sqrt(best) };
  }

  pointAt(s: number): { x: number; y: number; seg: number; ux: number; uy: number } {
    let d = s % this.length;
    if (d < 0) d += this.length;
    for (let i = 0; i < this.segs.length; i++) {
      const sg = this.segs[i];
      if (d <= sg.s0 + sg.len || i === this.segs.length - 1) {
        const t = clamp((d - sg.s0) / sg.len, 0, 1);
        return { x: sg.ax + sg.dx * t, y: sg.ay + sg.dy * t, seg: i, ux: sg.ux, uy: sg.uy };
      }
    }
    const sg = this.segs[0];
    return { x: sg.ax, y: sg.ay, seg: 0, ux: sg.ux, uy: sg.uy };
  }
}
